import { BaseTool, IToolContext, IToolManifest } from '../../../core/tools/interfaces/ITool';
import type { ICommand } from '../../../core/commands/ICommand';
import { CreateEdgeCommand } from '../../../core/commands/CreateEdgeCommand';
import { useDiagramStore } from '../../../stores/diagramStore';
import i18n from '@/i18n/config';
import { bindGenogramToolName } from './utils/toolNameI18n';
import { SessionMetrics } from '../../analytics/services/SessionMetrics';

export class CreateRelationshipTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'genogram-create-relationship',
    name: i18n.t('genogram:tools.createRelationship', { defaultValue: 'Create Relationship' }) as string,
    icon: '💕',
    category: 'create',
    description: 'Create a relationship between two people',
    version: '1.0.0',
    supportedDiagramTypes: ['genogram' as any],
    requiresSelection: true,
    minSelection: 2,
    maxSelection: 2,
    ui: {
      toolbar: false,
      contextMenu: false,
      propertyPanel: false,
      sidebar: false,
      floatingPanel: true,
      statusBar: false
    },
    applicability: {
      contextTypes: ['person'],
      diagramTypes: ['genogram'],
    },
    capabilities: {
      modifiesShapes: false,
      createsShapes: false,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: true,
      deletesConnectors: false,
      requiresUserInput: false,
      supportsMultiSelect: true,
      supportsBatchOperations: false
    },
    shortcuts: [
      { key: '1', description: 'Create relationship (when two people selected)' }
    ]
  };

  constructor() {
    super();
    bindGenogramToolName(this.manifest, 'tools.createRelationship', 'Create Relationship');
  }

  canExecute(context: IToolContext): boolean {
    if (!super.canExecute(context)) return false;

    // Check that selection is exactly 2 people
    const summary = context.selectionSummary;
    return summary?.contextType === 'person' && context.selection.size === 2;
  }

  execute(params?: { relationshipType?: string }): ICommand | null {
    if (!this.context) return null;

    const [fromId, toId] = Array.from(this.context.selection);
    const relationshipType = params?.relationshipType || 'marriage';

    const state = useDiagramStore.getState();
    const fromNode = state.nodes.find(n => n.id === fromId);
    const toNode = state.nodes.find(n => n.id === toId);

    if (!fromNode || !toNode) return null;

    const fromPos = { x: fromNode.x, y: fromNode.y };
    const toPos = { x: toNode.x, y: toNode.y };

    // Determine anchor points based on relative positions
    let fromAnchor: 'top' | 'right' | 'bottom' | 'left' = 'right';
    let toAnchor: 'top' | 'right' | 'bottom' | 'left' = 'left';

    if (Math.abs(fromPos.x - toPos.x) < 30) {
      // Vertically aligned
      if (fromPos.y < toPos.y) {
        fromAnchor = 'bottom';
        toAnchor = 'top';
      } else {
        fromAnchor = 'top';
        toAnchor = 'bottom';
      }
    } else if (fromPos.x < toPos.x) {
      // Left to right
      fromAnchor = 'right';
      toAnchor = 'left';
    } else {
      // Right to left
      fromAnchor = 'left';
      toAnchor = 'right';
    }

    const edgeCommand = new CreateEdgeCommand({
      type: relationshipType,
      from: fromId,
      to: toId,
      category: 'genogram',
      fromAnchor,
      toAnchor,
    });

    // Track connector creation (aggregated + first-time)
    SessionMetrics.increment('connectors_created');
    SessionMetrics.firstTime('connector_created');

    return edgeCommand;
  }
}
